/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  AlertTitle,
  Button as MuiButton,
  Card,
  CardContent,
  CardActions,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoIcon from '@mui/icons-material/Info';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { expertApi } from '../../services/api';
import { PageHeader, LoadingSpinner, ErrorAlert, ConfirmDialog } from '../common';
import { ExpertForm } from './ExpertForm';
import { toast } from 'react-toastify';
import type { Expert, ExpertListItem } from '../../types';

const ExpertList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [formOpen, setFormOpen] = useState(false);
  const [editingExpert, setEditingExpert] = useState<Expert | undefined>();
  const [deletingExpert, setDeletingExpert] = useState<ExpertListItem | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { data, isLoading, error } = useQuery({
    queryKey: ['experts', page, rowsPerPage],
    queryFn: () => expertApi.list({ page: page + 1, page_size: rowsPerPage }),
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const deleteMutation = useMutation({
    mutationFn: (id: number) => expertApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['experts'] });
      queryClient.invalidateQueries({ queryKey: ['systems'] });
      toast.success('Expert deleted successfully');
      setDeletingExpert(null);
    },
    onError: (error: any) => {
      console.error('Failed to delete expert:', error);
      
      // If it's a 404, the expert was already deleted - refresh the list
      if (error.response?.status === 404) {
        console.log('Expert already deleted, refreshing list...');
        queryClient.invalidateQueries({ queryKey: ['experts'] });
        queryClient.invalidateQueries({ queryKey: ['systems'] });
        toast.info('Expert was already deleted. List refreshed.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to delete expert');
      }
      
      setDeletingExpert(null);
    },
  });

  if (error) {
    return <ErrorAlert error={error as Error} />;
  }

  return (
    <Box>
      <PageHeader title="Expert Library" />

      {/* Informational alert about expert creation */}
      <Alert 
        severity="info" 
        icon={<InfoIcon />}
        sx={{ mb: { xs: 2, sm: 3 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
        action={
          <MuiButton 
            color="inherit" 
            size="small"
            onClick={() => navigate('/ai/systems')}
            sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
          >
            Go to AI Systems
          </MuiButton>
        }
      >
        <AlertTitle sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>How to Create Experts</AlertTitle>
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          Experts are created within AI Systems where they can reference other experts as inputs. 
          To create a new expert, open an AI System and use the Expert Canvas to add experts to your workflow.
        </Box>
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
          Create experts within AI Systems using the Expert Canvas.
        </Box>
      </Alert>

      <ExpertForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingExpert(undefined);
        }}
        expert={editingExpert}
      />

      <ConfirmDialog
        open={!!deletingExpert}
        title="Delete Expert"
        message={`Are you sure you want to delete "${deletingExpert?.name}"? This action cannot be undone.`}
        onConfirm={() => {
          if (deletingExpert) {
            deleteMutation.mutate(deletingExpert.id);
          }
        }}
        onCancel={() => setDeletingExpert(null)}
      />

      {isLoading ? (
        <LoadingSpinner message="Loading experts..." />
      ) : isMobile ? (
        /* Mobile Card View */
        <Box>
          <Grid container spacing={1.5}>
            {data?.results.map((expert) => (
              <Grid size={{ xs: 12, sm: 6 }} key={expert.id}>
                <Card>
                  <CardContent sx={{ p: 1.5, pb: 0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.9rem', pr: 1, wordBreak: 'break-word' }}>
                        {expert.name}
                      </Typography>
                      <Chip
                        label={expert.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        color={expert.is_active ? 'success' : 'default'}
                        sx={{ fontSize: '0.65rem', height: 20, flexShrink: 0 }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                      <Chip
                        label={expert.expert_type_display}
                        size="small"
                        color={
                          expert.expert_type === 'ANALYSIS' 
                            ? 'primary' 
                            : expert.expert_type === 'CODE' 
                              ? 'warning' 
                              : expert.expert_type === 'AGENT'
                                ? 'info'
                                : 'secondary'
                        }
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 20 }}
                      />
                      {expert.system_name && (
                        <Chip 
                          label={expert.system_name} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: 20 }}
                        />
                      )}
                      {expert.use_evaluator && (
                        <Chip
                          icon={<FactCheckIcon sx={{ fontSize: '0.8rem !important' }} />}
                          label="Eval"
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: 20 }}
                        />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      Model: {expert.model_name || '-'}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end', px: 1, py: 0.5 }}>
                    {expert.ai_system && (
                      <Tooltip title="Go to AI System">
                        <IconButton size="small" onClick={() => navigate('/ai/systems')}>
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={async () => {
                          const fullExpert = await expertApi.get(expert.id);
                          setEditingExpert(fullExpert);
                          setFormOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeletingExpert(expert)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
          {data?.results.length === 0 && (
            <Card sx={{ mt: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <InfoIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2">No experts found.</Typography>
                <Typography variant="caption" color="text.secondary">
                  Create an AI System and add experts to get started.
                </Typography>
              </CardContent>
            </Card>
          )}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={data?.count || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontSize: '0.75rem'
              }
            }}
          />
        </Box>
      ) : (
        /* Desktop Table View */
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>System</TableCell>
                <TableCell>Evaluator</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.results.map((expert) => (
                <TableRow key={expert.id} hover>
                  <TableCell>{expert.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={expert.expert_type_display}
                      size="small"
                      color={
                        expert.expert_type === 'ANALYSIS' 
                          ? 'primary' 
                          : expert.expert_type === 'CODE' 
                            ? 'warning' 
                            : expert.expert_type === 'AGENT'
                              ? 'info'
                              : 'secondary'
                      }
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{expert.model_name || '-'}</TableCell>
                  <TableCell>
                    {expert.system_name ? (
                      <Chip 
                        label={expert.system_name} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    ) : (
                      <Chip 
                        label="No System" 
                        size="small" 
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {expert.use_evaluator ? (
                      <Tooltip title={expert.evaluator_model_name || 'Uses expert model'}>
                        <Chip
                          icon={<FactCheckIcon />}
                          label="On"
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      </Tooltip>
                    ) : (
                      <Chip
                        label="Off"
                        size="small"
                        variant="outlined"
                        sx={{ color: 'text.disabled' }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={expert.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={expert.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {expert.ai_system && (
                      <Tooltip title="Go to AI System">
                        <IconButton
                          size="small"
                          onClick={() => navigate('/ai/systems')}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={async () => {
                          // Fetch full expert details for editing
                          const fullExpert = await expertApi.get(expert.id);
                          setEditingExpert(fullExpert);
                          setFormOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeletingExpert(expert)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {data?.results.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Box>
                      <InfoIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Box>No experts found.</Box>
                      <Box sx={{ mt: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
                        Create an AI System and add experts to get started.
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={data?.count || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}
    </Box>
  );
};

export default ExpertList;
